import { Component, OnInit, ViewChild } from '@angular/core';
import { DataSourcesService } from '../../Services/DataProcess/datasources.service';
import { LoginService } from '../../Services/login.service';
import { ProductDataSource } from '../../Model/dataProcess/productDataSource';
import { SystemService } from '../../Services/system.service';
import { Brand } from '../../Model/Dto/Brand/Brand';
import { RowInsertedEvent, RowRemovedEvent, RowUpdatedEvent, SavedEvent, ToolbarPreparingEvent } from 'devextreme/ui/data_grid';
import { TranslateService } from '@ngx-translate/core';
import { custom } from 'devextreme/ui/dialog';
import { ViewService } from 'app/Services/view.service';
import { ValueChangedEvent } from 'devextreme/ui/text_box';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { OAuthService } from 'angular-oauth2-oidc';
import { BeforeSendEvent } from 'devextreme/ui/file_uploader';
import { DxFileUploaderTypes } from 'devextreme-angular/ui/file-uploader';
import { Address } from '../../Model/Catalog/Address';
import { DxDataGridComponent } from 'devextreme-angular';
import { JobService } from '../../Services/job.service';

@Component({
  selector: 'app-datasources',
  templateUrl: './datasources.component.html',
  styleUrls: ['./datasources.component.css']
})
export class DatasourcesComponent implements OnInit {

  @ViewChild('dataSourceGrid', { static: false })
  dataSourceGrid!: DxDataGridComponent;

  productDataSources: ProductDataSource[] = [];
  relatedBrands: Brand[] = [];

  types = [
    { value: 'Manufacturer', text: this.translate.instant('Hersteller') },
    { value: 'Supplier',     text: this.translate.instant('Lieferant') },
  ];
  countryCodes: { code: string; name: string; label: string }[] = [];
  popupVisible: boolean = false;
  newOrUpdateProductDataSource: ProductDataSource;
  oldProductDataSource: ProductDataSource;
  updatedDataSources: { oldDataSource: ProductDataSource; newDataSource: ProductDataSource }[] = [];
  editMode: boolean = false;
  notSyncedDataSourceExists: boolean = false;
  isEuCountry: boolean = false;
  selectedRows: any;
  hasSelection: boolean;
  applyButtonHint: string = "";
  startMacroDialogVisible: boolean = false;
  selectedMacroStartDate: Date;
  setBorderColor: boolean = false;

  EU_CODES = new Set<string>([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK',
    'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
    'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT',
    'RO', 'SK', 'SI', 'ES', 'SE'
  ]);

  importExcelVisible: boolean;

  constructor(
    public dataSourcesService: DataSourcesService,
    public loginService: LoginService,
    public translate: TranslateService,
    public systemService: SystemService,
    public oAuthService: OAuthService,
    public jobService: JobService
    ) {
    this.showAddOrUpdateDataSource = this.showAddOrUpdateDataSource.bind(this);
    this.startApplyChangesMakro = this.startApplyChangesMakro.bind(this);
    this.startJobListener = this.startJobListener.bind(this);
    this.reloadDataSources = this.reloadDataSources.bind(this);
  }

  async ngOnInit() {
    this.reloadDataSources(true);
    this.applyButtonHint = this.translate.instant("Neue Lieferanten oder Änderungen an Lieferanten werden auf alle betroffenen Kataloge angewendet.");
    this.updatedDataSources = [];
    this.newOrUpdateProductDataSource = {
      addressObj: new Address(),
      importerAddress: new Address(),
    } as ProductDataSource;
    ViewService.fetchIsoCountyCodes().then(countries => {
      const list = countries?.length ? countries : ViewService.dropDownTerritories;
      this.countryCodes = list.map(c => ({
        ...c,
        label: `${c.name} (${c.code})`
      }));
    });
  }

  typeDisplay = (row: any) => {
    switch (row?.type) {
      case 'Manufacturer': return this.translate.instant('Hersteller');
      case 'Supplier': return this.translate.instant('Lieferant');
      default: return row?.type ?? '';
    }
  };

  hasUnsyncedChanges(): boolean {
    return (this.updatedDataSources?.length ?? 0) > 0;
  }

  onRowPrepared(e: any) {
    if (e.rowType !== 'data' || !this.setBorderColor) return;
    const highlightRow = !e.data?.lastSync || new Date(e.data?.lastUpdate) > new Date(e.data?.lastSync);

    if (highlightRow) {
      const $els = Array.isArray(e.rowElement) ? e.rowElement : [e.rowElement];
      $els.forEach((el: any) => el.classList?.add('blue-row'));
    }
    this.setBorderColor = false;
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsyncedChanges()) {
      return true;
    }

    let unsavedChangesDialog = custom({
      title: this.translate.instant('Seite verlassen?'),
      messageHtml: this.translate.instant(
        'Es gibt noch nicht synchronisierte Änderungen. Seite wirklich verlassen?'
      ),
      buttons: [
        {
          text: this.translate.instant('Verlassen'),
          onClick: (e) => {
            return true;
          }
        },
        {
          text: this.translate.instant('Nicht verlassen'),
          onClick: (e) => {
            return false;
          }
        }
      ]
    });
    var result = await unsavedChangesDialog.show();
    return result;
  }

  onToolbarPreparing(e) {

    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'exportDataSourceButtonTemplate'
    });
    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'importDataSourceButtonTemplate'
    });
    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'newDataSourceButtonTemplate'
    });
    e.toolbarOptions.items.unshift({
      location: 'after',
      locateInMenu: 'auto',
      template: 'applyDataSourceChangesButtonTemplate'
    });
  }

  onCountryChanged(newCode: string): void {
    this.isEuCountry = this.EU_CODES.has(newCode);
  }

  deepClone<T>(obj: T): T {
    return typeof structuredClone === 'function'
      ? structuredClone(obj)
      : JSON.parse(JSON.stringify(obj));
  }

  async showAddOrUpdateDataSource(productDataSource?: ProductDataSource) {
    const empty = { addressObj: new Address(), importerAddress: new Address() } as ProductDataSource;

    const isEdit = !!productDataSource;
    const original = isEdit ? this.deepClone(productDataSource!) : empty;

    this.newOrUpdateProductDataSource = isEdit
      ? this.deepClone(productDataSource!)
      : empty;

    this.editMode = isEdit;
    this.relatedBrands = isEdit
      ? await this.dataSourcesService.getRelatedBrands(productDataSource!.id, this.loginService.currentCustomer.id)
      : [];

    if (!this.newOrUpdateProductDataSource.addressObj)
      this.newOrUpdateProductDataSource.addressObj = new Address();
    if (!this.newOrUpdateProductDataSource.importerAddress)
      this.newOrUpdateProductDataSource.importerAddress = new Address();

    const keyId = productDataSource?.id ?? this.newOrUpdateProductDataSource.id;
    const idx = this.updatedDataSources.findIndex(
      p => p.oldDataSource?.id === keyId || p.newDataSource?.id === keyId
    );

    if (idx > -1) {
      this.updatedDataSources[idx] = {
        ...this.updatedDataSources[idx],
        newDataSource: this.newOrUpdateProductDataSource
      };
    } else {
      this.updatedDataSources.push({
        oldDataSource: isEdit ? original : null,
        newDataSource: this.newOrUpdateProductDataSource
      });
    }

    this.popupVisible = true;
  }

  async onEditSaved(updatedDataSource: ProductDataSource) {
    this.notSyncedDataSourceExists = true;

    const idx = this.updatedDataSources.findIndex(
      p => p.oldDataSource?.id === updatedDataSource.id || p.newDataSource?.id === updatedDataSource.id
    );

    const newClone = this.deepClone(updatedDataSource);

    if (idx > -1) {
      this.updatedDataSources[idx] = {
        ...this.updatedDataSources[idx],
        newDataSource: newClone
      };
    } else {
      let oldDataSource = newClone;
      oldDataSource.description = oldDataSource.description + " old";
      this.updatedDataSources.push({
        oldDataSource: oldDataSource,
        newDataSource: newClone
      });
    }

    this.updatedDataSources = this.updatedDataSources.filter(x => x.oldDataSource !== null);

    this.productDataSources =
      await this.dataSourcesService.getAll(this.loginService.currentCustomer.id);
  }

  applyDataSourceChanges() {
    this.startMacroDialogVisible = true;
  }

  addAlternativeName(e: any) {
    const newName = e.text?.trim();
    if (!newName) {
      e.customItem = null;
      return;
    }

    let list = this.newOrUpdateProductDataSource.alternativeNames || [];
    if (!list.includes(newName)) {
      list = [...list, newName];
    }

    e.customItem = newName;
    e.component.option('dataSource', list);
    e.component.option('value', list);

    this.newOrUpdateProductDataSource.alternativeNames = list;
  }

  getSmileyEmoji(data: ProductDataSource): string {
    switch (this.getSecurityInformationDegree(data)) {
      case 2: return '😊\uFE0E';
      case 1: return '😐\uFE0E';
      default: return '\u2639\uFE0E';
    }
  }

  getSmileyClass(data: ProductDataSource): string {
    var degree = this.getSecurityInformationDegree(data);
    if (degree == 2) { return 'smiley-green'; }
    if (degree == 1) { return 'smiley-yellow'; }
    return 'smiley-red';
  }

  isFilled(value: string | string[] | null | undefined): boolean {
    if (Array.isArray(value)) { return value.length > 0; }
    return value != null && value.toString().trim() !== '';
  }

  getSecurityInformationDegree(ds: ProductDataSource): 0 | 1 | 2 {
    const requiredOk =
      this.isFilled(ds.name) &&
      this.isFilled(ds.type) &&
      this.isFilled(ds.kreditorNr) &&
      this.isFilled(ds.addressObj?.street) &&
      this.isFilled(ds.countryCode);

    if (!requiredOk) {
      return 0;
    }

    const contactOk =
      this.isFilled(ds.contactPerson) &&
      this.isFilled(ds.supportTel) &&
      this.isFilled(ds.supportMail);

    const websiteOk = this.isFilled(ds.website);

    return contactOk && websiteOk ? 2 : 1;
  }

  displayLastUpdate = (row: any) =>
    this.isDefaultDate(row?.lastUpdate) ? null : row.lastUpdate;

  displayLastSync = (row: any) =>
    this.isDefaultDate(row?.lastSync) ? null : row.lastSync;

  isDefaultDate(v: any) {
    return v === '0001-01-01T00:00:00Z' || (v && new Date(v).getUTCFullYear() === 1);
  }

  isApplyDataSourceButtonActiv() {
    return this.updatedDataSources.length > 0 || this.hasSelection;
  }

  applyButtonText(): string {
    var text = this.translate.instant('Alle Änderungen anwenden');
    this.applyButtonHint = this.translate.instant("Neue Lieferanten oder Änderungen an Lieferanten werden auf alle betroffenen Kataloge angewendet.");
    if (this.hasSelection) {
      text = this.translate.instant('Synchronisierung starten');
      this.applyButtonHint = this.translate.instant("Alle betroffenen Kataloge werden mit den ausgewählten Lieferanten synchronisiert.");
    }
    return text;
  }

  onSelectionChanged(e: any) {
    this.selectedRows = e.selectedRowsData;
    this.hasSelection = this.selectedRows.length;
    this.updatedDataSources = [];

    this.selectedRows.forEach((row: ProductDataSource) => this.updatedDataSources.push({ oldDataSource: row, newDataSource: row }));
  }

  async onUploaded(e: DxFileUploaderTypes.UploadedEvent) {
    this.newOrUpdateProductDataSource.logoFile = e.request.responseText as string;
    this.reloadDataSources();
  }

  async startApplyChangesMakro() {
    var oldToNewDataSources = this.updatedDataSources;
    this.updatedDataSources = [];
    this.dataSourceGrid?.instance.clearSelection();
    this.selectedRows = [];
    this.hasSelection = false;
    await this.dataSourcesService.startApplyChangesToAccountMakro(
      { oldToNewDataSources: oldToNewDataSources, scheduledFor: this.selectedMacroStartDate },
      this.loginService.currentCustomer.id
    );
    this.startMacroDialogVisible = false;
    this.startJobListener();
  }

  async onRowRemoved(event: RowRemovedEvent) {
    let key = event.data.id;
    await this.dataSourcesService.delete(key, this.loginService.currentCustomer.id);
    this.reloadDataSources();
  }

  onBeforeSendUpload(e: { request: XMLHttpRequest }) {
    e.request.setRequestHeader("customerId", this.loginService.currentUser?.customerId);
    e.request.withCredentials = true;
    e.request.setRequestHeader('Authorization', 'Bearer ' + this.oAuthService.getAccessToken());
  }

  onImportUploaded() {
    this.startJobListener(true);
    this.importExcelVisible = false;
    this.systemService.notifyInfo(this.translate.instant("Import Verarbeitung – Bitte haben Sie einen Moment Geduld, gerade werden Ihre Daten im Hintergrund verarbeitet."));
  }

  async startJobListener(updateUpdatedDataSources: boolean = false) {
    var isRunning = await this.jobService.isJobRunningThatChangesDatasourceList(this.loginService.currentCustomer.id);
    if (isRunning) {
      setTimeout(() => this.startJobListener(updateUpdatedDataSources), 2000);
    }
    else {
      this.reloadDataSources(updateUpdatedDataSources);
    }
  }

  exportDataSource() {
    this.systemService.notifyInfo("Lieferanten Export gestartet");
    this.dataSourcesService.startExcelExport(this.loginService.currentCustomer.id);
  }

  showImportDataSource() {
    this.importExcelVisible = true;
  }

  async reloadDataSources(updateUpdatedDataSources: boolean = false) {
    this.productDataSources = await this.dataSourcesService.getAll(this.loginService.currentCustomer.id);
    this.notSyncedDataSourceExists = this.productDataSources.some(item => !item.lastSync || new Date(item.lastUpdate) > new Date(item.lastSync));

    if (updateUpdatedDataSources) {
      this.updatedDataSources = [];
      var newOrChanged = this.productDataSources.filter(item => new Date(item.lastUpdate) > new Date(item.lastSync));
      newOrChanged.forEach((ds: ProductDataSource) => this.updatedDataSources.push({ oldDataSource: ds, newDataSource: ds }));
      this.setBorderColor = true;
    }
  }
}
